import { useState } from "react";
import { cn } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { 
  Plus, Pencil, Trash2, RotateCw, Search, 
  Calendar, Timer, Trophy, ChevronRight,
  ClipboardList, BookOpen, Clock, Settings2
} from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { SearchBar } from "@/components/common/SearchBar";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Card, 
  CardContent, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { useListState } from "@/hooks/useListState";
import { 
  useGetWeeklyTestsQuery, 
  useCreateWeeklyTestMutation, 
  useUpdateWeeklyTestMutation, 
  useDeleteWeeklyTestMutation,
  WeeklyTest 
} from "@/redux/slices/weeklyTestApi";
import { useGetAcademicYearsQuery } from "@/redux/slices/academicYearApi";
import { format } from "date-fns";

const schema = z.object({
  title: z.string().min(2, "Title required"),
  description: z.string().optional().default(""),
  testDate: z.string().min(1, "Date required"),
  timeLimit: z.coerce.number().min(1, "Required"),
  totalMarks: z.coerce.number().min(1, "Required"),
  passingMarks: z.coerce.number().min(1, "Required"),
  academicYearId: z.string().min(1, "Year required"),
  isActive: z.boolean().default(true),
});
type FormValues = z.infer<typeof schema>;

export default function WeeklyExamsPage() {
  const list = useListState();
  const { data, isFetching, refetch } = useGetWeeklyTestsQuery(list.queryParams());
  const { data: yearsData } = useGetAcademicYearsQuery({ limit: 100 });
  
  const [createTest, createM] = useCreateWeeklyTestMutation();
  const [updateTest, updateM] = useUpdateWeeklyTestMutation();
  const [deleteTest, deleteM] = useDeleteWeeklyTestMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<WeeklyTest | null>(null);
  const [toDelete, setToDelete] = useState<WeeklyTest | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { 
      title: "", description: "", testDate: new Date().toISOString().split('T')[0],
      timeLimit: 60, totalMarks: 100, passingMarks: 35, academicYearId: "", isActive: true 
    },
  });

  const openCreate = () => {
    setEditing(null);
    form.reset({ 
       title: "", description: "", testDate: new Date().toISOString().split('T')[0],
       timeLimit: 60, totalMarks: 100, passingMarks: 35, academicYearId: "", isActive: true 
    });
    setOpen(true);
  };

  const openEdit = (t: WeeklyTest) => {
    setEditing(t);
    const formattedDate = new Date(t.testDate).toISOString().split('T')[0];
    form.reset({ ...t, testDate: formattedDate } as any);
    setOpen(true);
  };

  const onSubmit = async (v: FormValues) => {
    try {
      if (editing) {
        await updateTest({ id: editing.id, body: v }).unwrap();
        toast.success("Exam schedule updated");
      } else {
        await createTest(v).unwrap();
        toast.success("New weekly exam scheduled");
      }
      setOpen(false);
    } catch {
      toast.error("Error saving examination details");
    }
  };

  return (
    <div className="min-h-screen bg-muted/20">
      <PageHeader
        title="Weekly Exams"
        description="Schedule and manage comprehensive weekly assessments"
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching}>
              <RotateCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
            </Button>
            <Button onClick={openCreate} className="shadow-lg shadow-primary/20">
              <Plus className="mr-1.5 h-4 w-4" /> New Exam
            </Button>
          </div>
        }
      />

      <div className="space-y-6 p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-4 rounded-2xl border bg-card p-4 shadow-sm lg:flex-row lg:items-center">
          <SearchBar 
            value={list.search} 
            onChange={list.setSearch} 
            placeholder="Search exams by title..." 
            className="flex-1" 
          />
          <div className="flex flex-wrap items-center gap-3">
            <Select value={list.filters.academicYearId || "all"} onValueChange={(v) => list.setFilter("academicYearId", v === "all" ? undefined : v)}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All Academic Years" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Academic Years</SelectItem>
                {yearsData?.data?.map(y => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {isFetching ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-64 animate-pulse rounded-2xl bg-card/60 backdrop-blur-sm border border-border/50" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {data?.data?.map((test) => (
              <Card key={test.id} className="group relative overflow-hidden bg-card/60 backdrop-blur-sm shadow-sm ring-1 ring-border hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/5 transition-all duration-300">
                <div className="absolute top-0 left-0 h-1 w-full bg-primary/20 transition-all group-hover:bg-primary" />
                
                <CardHeader className="p-6 pb-4">
                   <div className="flex items-start justify-between">
                      <div className="space-y-1.5">
                         <CardTitle className="text-xl font-bold leading-tight group-hover:text-primary transition-colors">{test.title}</CardTitle>
                         <div className="flex items-center gap-2 text-xs text-muted-foreground font-bold uppercase tracking-wider">
                            <Calendar className="h-3.5 w-3.5 text-primary" /> {format(new Date(test.testDate), "dd MMM yyyy")}
                         </div>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity translate-x-2 group-hover:translate-x-0">
                        <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full hover:bg-primary/10 hover:text-primary" onClick={() => openEdit(test)}>
                          <Settings2 className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full hover:bg-destructive/10 hover:text-destructive" onClick={() => setToDelete(test)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                   </div>
                </CardHeader>

                <CardContent className="px-6 py-2">
                   <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="space-y-1 rounded-xl bg-muted/30 p-3 shadow-inner">
                         <p className="text-[9px] uppercase font-bold text-muted-foreground tracking-widest">Duration</p>
                         <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-primary" />
                            <p className="text-sm font-bold">{test.timeLimit} Min</p>
                         </div>
                      </div>
                      <div className="space-y-1 rounded-xl bg-muted/30 p-3 shadow-inner">
                         <p className="text-[9px] uppercase font-bold text-muted-foreground tracking-widest">Passing</p>
                         <div className="flex items-center gap-2">
                            <Trophy className="h-4 w-4 text-yellow-500" />
                            <p className="text-sm font-bold">{test.passingMarks}/{test.totalMarks}</p>
                         </div>
                      </div>
                   </div>
                   {test.description && (
                     <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed italic mb-2">"{test.description}"</p>
                   )}
                </CardContent>

                <CardFooter className="p-6 pt-4 border-t bg-muted/5">
                   <Button variant="default" className="w-full gap-2 text-xs font-bold uppercase tracking-widest h-10 shadow-lg shadow-primary/5 group/btn" size="sm">
                      <ClipboardList className="h-4 w-4 transition-transform group-hover/btn:scale-110" /> 
                      Manage Questions
                   </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl shadow-2xl border-none p-0 flex flex-col rounded-2xl max-h-[90vh]">
           <DialogHeader className="p-8 pb-4 bg-primary/5 text-left">
              <DialogTitle className="text-2xl font-bold">{editing ? "Update Examination" : "Schedule Weekly Exam"}</DialogTitle>
              <DialogDescription className="font-medium text-muted-foreground">Configure global assessment parameters and scheduling.</DialogDescription>
           </DialogHeader>
           <div className="px-8 py-4 overflow-y-auto flex-1">
            <form id="weekly-form" onSubmit={form.handleSubmit(onSubmit)} className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="sm:col-span-2 space-y-2">
                  <Label className="text-sm font-semibold text-foreground">Exam Title</Label>
                  <Input className="h-11 shadow-sm" placeholder="e.g. Weekly Assessment - Week 12" {...form.register("title")} />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">Examination Date</Label>
                  <Input type="date" className="h-11 shadow-sm" {...form.register("testDate")} />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">Academic Year</Label>
                  <Select value={form.watch("academicYearId") || undefined} onValueChange={(v) => form.setValue("academicYearId", v)}>
                      <SelectTrigger className="h-11 shadow-sm">
                        <SelectValue placeholder="Select Year" />
                      </SelectTrigger>
                      <SelectContent>
                        {yearsData?.data?.map(y => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}
                      </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">Time Limit (Minutes)</Label>
                  <Input type="number" className="h-11 shadow-sm font-mono" {...form.register("timeLimit")} />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">Total Marks</Label>
                  <Input type="number" className="h-11 shadow-sm font-mono" {...form.register("totalMarks")} />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">Passing Marks</Label>
                  <Input type="number" className="h-11 shadow-sm font-mono" {...form.register("passingMarks")} />
                </div>
                <div className="sm:col-span-2 space-y-2">
                  <Label className="text-sm font-semibold text-foreground">Additional Instructions (Optional)</Label>
                  <Textarea rows={3} className="resize-none shadow-sm" placeholder="Provide extra details for students..." {...form.register("description")} />
                </div>
            </form>
           </div>
           <DialogFooter className="border-t p-8 bg-muted/10">
              <Button variant="ghost" onClick={() => setOpen(false)} className="h-11 px-6 font-bold uppercase tracking-tight text-xs">Cancel</Button>
              <Button form="weekly-form" type="submit" disabled={createM.isLoading || updateM.isLoading} className="h-11 px-8 rounded-xl font-bold uppercase tracking-wider text-xs shadow-xl shadow-primary/20 group">
                {editing ? "Save Changes" : "Save Schedule"}
                <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
           </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        onConfirm={async () => { 
          if (toDelete) { 
            await deleteTest(toDelete.id).unwrap(); 
            toast.success("Weekly exam permanently removed"); 
            setToDelete(null); 
          } 
        }}
        loading={deleteM.isLoading}
        title="Delete weekly exam?"
        description={`"${toDelete?.title}" and its associated question assignments will be deleted. This cannot be undone.`}
      />
    </div>
  );
}
